/**
 * AudienceRedirect — stable entry path that resolves to the right surface for
 * the current audience.
 *
 * Used for partner deep links (Arena ID) where a single path must work for
 * signed-out visitors and signed-in learners alike without an unexpected
 * bounce. Query strings (utm_*) are preserved so attribution survives the hop.
 */

import { useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasStoredSession } from "@/lib/auth/sessionHint";

interface AudienceRedirectProps {
  /** Destination for visitors with no session. */
  publicTo: string;
  /** Destination for signed-in learners. */
  authedTo: string;
}

export function AudienceRedirect({ publicTo, authedTo }: AudienceRedirectProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const storedSession = useRef(hasStoredSession()).current;

  const authed = isAuthenticated || (isLoading && storedSession);
  const target = authed ? authedTo : publicTo;
  const search = location.search || "";

  return <Navigate to={`${target}${search}`} replace />;
}
